import React from "react";
import "./PrivacyPolicy.css";
import Logo from "../assets/images/takeStock_logo.png";

const SUPPORT_EMAIL = "support@takestockinventory.com";
const EFFECTIVE_DATE = "June 3, 2025";
const APPLE_EULA_URL =
  "https://www.apple.com/legal/internet-services/itunes/dev/stdeula/";

function PrivacyPolicy() {
  return (
    <div className="pp-page">
      <section className="pp-hero">
        <div className="pp-card">
          <img src={Logo} alt="TakeStock" className="pp-logo" />
          <p className="pp-kicker">Legal &amp; Privacy</p>
          <h1>Privacy Policy</h1>
          <p className="pp-lead">
            TakeStock ("we", "us", or "our") is committed to protecting your
            privacy. This policy explains what information we collect, how we
            use it, and your rights — including in relation to our subscription
            service.
          </p>
          <p className="pp-meta">Effective date: {EFFECTIVE_DATE}</p>
        </div>
      </section>

      <main className="pp-content">
        {/* ── 1. About TakeStock ── */}
        <section className="pp-section">
          <h2>1. About TakeStock</h2>
          <p>
            TakeStock is a home-inventory mobile application that helps
            individuals, families, insurance professionals, and estate managers
            document, organise, and manage personal property. Users can
            photograph items, assign categories and values, generate reports,
            and share inventory access with authorised parties (e.g. estate
            executors or insurance adjusters).
          </p>
        </section>

        {/* ── 2. Information We Collect ── */}
        <section className="pp-section">
          <h2>2. Information We Collect</h2>

          <h3>Account &amp; Profile Information</h3>
          <ul>
            <li>User name, email address, and password (stored as a secure hash).</li>
            <li>Profile photo (optional).</li>
            <li>Property information.</li>
          </ul>

          <h3>Inventory &amp; Property Data</h3>
          <ul>
            <li>Item names, descriptions, categories, estimated values, and quantities.</li>
            <li>Photos of items and rooms.</li>
            <li>Property addresses and room names entered by you.</li>
          </ul>

          <h3>Subscription &amp; Payment Information</h3>
          <ul>
            <li>
              Subscription plan and billing status. Payment transactions are
              processed entirely by Apple (iOS) and we do not store full card or
              bank details on our servers.
            </li>
            <li>Purchase receipts and subscription period provided by Apple.</li>
          </ul>

          <h3>Device &amp; Usage Data</h3>
          <ul>
            <li>Device type, operating system version, and app version.</li>
            <li>Log data such as access timestamps, feature usage, and error reports.</li>
            <li>
              Camera and photo-library permissions — used only when you
              explicitly capture or upload an image.
            </li>
          </ul>

          <h3>Communications</h3>
          <ul>
            <li>
              Messages you send to our support team at{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
            </li>
          </ul>
        </section>

        {/* ── 3. How We Use Your Information ── */}
        <section className="pp-section">
          <h2>3. How We Use Your Information</h2>
          <ul>
            <li>To create and manage your account and authenticate your identity.</li>
            <li>To store, display, and organise your inventory data.</li>
            <li>
              To process and manage your subscription, verify entitlements, and
              provide access to paid features.
            </li>
            <li>
              To generate reports (PDF/print) that you can share with insurers,
              legal representatives, or family members.
            </li>
            <li>To send transactional emails (e.g. password resets, account notices).</li>
            <li>
              To improve the app through aggregated, anonymised analytics — we do
              not sell individual usage data.
            </li>
            <li>To respond to support requests and troubleshoot issues.</li>
            <li>
              To comply with legal obligations, prevent fraud, and protect the
              security of the service.
            </li>
          </ul>
        </section>

        {/* ── 4. Subscription Model ── */}
        <section className="pp-section">
          <h2>4. Subscription Model</h2>
          <p>
            TakeStock offers auto-renewable subscriptions managed through Apple
            In-App Purchases. By subscribing you agree to the following terms:
          </p>
          <ul>
            <li>
              <strong>Billing:</strong> Your subscription fee is charged to your
              Apple ID at the time of purchase confirmation and automatically
              renews at the end of each subscription period unless cancelled at
              least 24 hours before the renewal date.
            </li>
            <li>
              <strong>Renewal:</strong> Your Apple ID will be charged for
              renewal within 24 hours before the end of the current period.
            </li>
            <li>
              <strong>Managing &amp; cancelling:</strong> You can manage or turn
              off auto-renewal in your Apple ID Account Settings at any time
              after purchase.
            </li>
            <li>
              <strong>Free trial (if offered):</strong> Any unused portion of a
              free trial period will be forfeited when you purchase a
              subscription.
            </li>
            <li>
              <strong>Refunds:</strong> Refund requests are handled by Apple in
              accordance with their standard refund policy.
            </li>
          </ul>
          <p>
            For full subscription terms please review our{" "}
            <a href={APPLE_EULA_URL} target="_blank" rel="noreferrer">
              Terms of Use (EULA)
            </a>
            .
          </p>
        </section>

        {/* ── 5. Data Sharing ── */}
        <section className="pp-section">
          <h2>5. Data Sharing &amp; Disclosure</h2>
          <p>We do not sell your personal data. We may share data only:</p>
          <ul>
            <li>
              <strong>With service providers</strong> (cloud hosting, email
              delivery, analytics) who are contractually obligated to protect
              your data.
            </li>
            <li>
              <strong>With people you invite</strong> — portal users or estate
              executors you grant access to will see the inventory data for that
              specific project.
            </li>
            <li>
              <strong>With Apple</strong> for in-app purchase verification and
              subscription management.
            </li>
            <li>
              <strong>When required by law</strong> — to comply with a legal
              obligation, court order, or to protect the rights and safety of
              TakeStock users.
            </li>
          </ul>
        </section>

        {/* ── 6. Data Retention ── */}
        <section className="pp-section">
          <h2>6. Data Retention</h2>
          <p>
            We retain your data for as long as your account is active or as
            needed to provide you with the service. If you delete your account:
          </p>
          <ul>
            <li>Your account and inventory data are deactivated and marked for deletion.</li>
            <li>
              Subscription, billing, and financial records are retained only for
              the period required by applicable legal or tax obligations.
            </li>
            <li>
              Security and audit logs may be retained for a limited period to
              protect against fraud and abuse.
            </li>
          </ul>
          <p>
            To request account deletion, visit our{" "}
            <a href="/delete-account">Account Deletion page</a> or email{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
          </p>
        </section>

        {/* ── 7. Security ── */}
        <section className="pp-section">
          <h2>7. Security</h2>
          <p>
            We use industry-standard measures including encrypted data
            transmission (HTTPS/TLS), password hashing, and access controls to
            protect your information. No method of transmission or storage is
            100% secure; we encourage you to use a strong, unique password and
            keep your device secure.
          </p>
        </section>

        {/* ── 8. Children's Privacy ── */}
        <section className="pp-section">
          <h2>8. Children's Privacy</h2>
          <p>
            TakeStock is not directed at children under 13(or the minimum age required by applicable law in the user's jurisdiction). We do not knowingly
            collect personal information from children. If you believe a child
            has provided us with personal information, please contact us and we
            will delete it promptly.
          </p>
        </section>

        {/* ── 9. Your Rights ── */}
        <section className="pp-section">
          <h2>9. Your Rights</h2>
          <p>
            Depending on your location, you may have the right to access,
            correct, or delete your personal data, or to restrict or object to
            certain processing. To exercise these rights, contact us at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>. We will
            respond within 30 days.
          </p>
        </section>

        {/* ── 10. Changes to This Policy ── */}
        <section className="pp-section">
          <h2>10. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. When we do, we
            will revise the effective date at the top of this page. We encourage
            you to review this policy periodically. Continued use of TakeStock
            after changes are posted constitutes acceptance of the updated
            policy.
          </p>
        </section>

        {/* ── 11. Legal Links ── */}
        <section className="pp-section pp-section--links">
          <h2>11. Legal Links</h2>
          <div className="pp-legal-links">
            <a
              href={APPLE_EULA_URL}
              target="_blank"
              rel="noreferrer"
              className="pp-legal-link"
            >
              Terms of Use (EULA)
            </a>
            <a href="/privacy-policy" className="pp-legal-link pp-legal-link--active">
              Privacy Policy
            </a>
            <a href="/delete-account" className="pp-legal-link">
              Account Deletion
            </a>
          </div>
        </section>

        {/* ── 12. Contact ── */}
        <section className="pp-section">
          <h2>12. Contact Us</h2>
          <p>
            If you have questions or concerns about this Privacy Policy or your
            data, please contact:
          </p>
          <p>
            <strong>TakeStock Support</strong>
            <br />
            <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        </section>
      </main>
    </div>
  );
}

export default PrivacyPolicy;
